import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canAccessSupportRecords } from "@/lib/permissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Heart, MessageCircle, Plus, CalendarDays, MapPin, Clock, Newspaper, Pencil, Trash2, Users,
} from "lucide-react";

const SCHOOL_BANNER = "https://customer-assets-7cd3h4nn.emergentagent.net/job_dorm-attendance-4/artifacts/2b369d0212dbe2f5_1791344049328.jpg";

const CATS = ["General", "Facilities", "Event", "Notice", "Urgent"];
const catColor = {
  General: "bg-slate-100 text-slate-700",
  Facilities: "bg-sky-100 text-sky-700",
  Event: "bg-indigo-100 text-indigo-700",
  Notice: "bg-amber-100 text-amber-700",
  Urgent: "bg-red-100 text-red-700",
};

const timeAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [residents, setResidents] = useState([]);
  const [operations, setOperations] = useState(null);
  const [newsForm, setNewsForm] = useState({ category: "General", title: "", content: "", image: "" });
  const [newsSearch, setNewsSearch] = useState("");
  const [newsCategoryFilter, setNewsCategoryFilter] = useState("all");
  const [commentText, setCommentText] = useState({});
  const [editingPost, setEditingPost] = useState(null);
  const [editForm, setEditForm] = useState({ category: "General", title: "", content: "", image: "" });
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const load = async () => {
    const [n, e, s, o] = await Promise.all([
      api.get("/news"), api.get("/events"), api.get("/dashboard/status"), api.get("/dashboard/operations"),
    ]);
    setNews(n.data); setEvents(e.data); setResidents(s.data);
    setOperations(o.data);
  };
  useEffect(() => { load(); }, []);

  const postNews = async () => {
    if (!newsForm.title.trim()) return toast.error("Title is required");
    await api.post("/news", newsForm);
    setNewsForm({ category: "General", title: "", content: "", image: "" });
    toast.success("Posted to the bulletin!");
    load();
  };

  const toggleLike = async (id) => {
    const res = await api.post(`/news/${id}/like`);
    setNews((prev) => prev.map((p) => (p.id === id ? { ...p, likes: res.data.likes } : p)));
  };

  const addComment = async (id) => {
    const text = (commentText[id] || "").trim();
    if (!text) return;
    const res = await api.post(`/news/${id}/comment`, { text });
    setNews((prev) => prev.map((p) => (p.id === id ? { ...p, comments: [...(p.comments || []), res.data] } : p)));
    setCommentText({ ...commentText, [id]: "" });
  };

  const openEditPost = (post) => {
    setEditingPost(post);
    setEditForm({ category: post.category, title: post.title, content: post.content || "", image: post.image || "" });
    setEditDialogOpen(true);
  };

  const selectImage = async (event, setImage) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be 5 MB or smaller");

    try {
      const image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Unable to read image"));
        reader.readAsDataURL(file);
      });
      setImage(image);
    } catch {
      toast.error("Unable to read image");
    }
  };

  const savePost = async () => {
    if (!editForm.title.trim()) return toast.error("Title is required");
    try {
      await api.put(`/news/${editingPost.id}`, editForm);
      setEditDialogOpen(false);
      await load();
      toast.success("Post updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update post");
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete “${post.title}”?`)) return;
    try {
      await api.delete(`/news/${post.id}`);
      setNews((current) => current.filter((item) => item.id !== post.id));
      toast.success("Post deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete post");
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  const toDateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const weekStartKey = toDateKey(weekStart);
  const weekEndKey = toDateKey(weekEnd);
  const weekEvents = events.filter((event) => {
    const eventStart = event.start_date || event.date;
    const eventEnd = event.end_date || eventStart;
    return eventEnd >= weekStartKey && eventStart < weekEndKey;
  });
  const availableResidents = residents.filter((resident) => resident.role?.toLowerCase() !== "visitor");
  const isResidentOnDuty = (resident) => ["Online", "Available", "On-duty"].includes(resident.status);
  const getResidentStatusLabel = (resident) => (isResidentOnDuty(resident) ? "On-duty" : "Off-duty");
  const filteredNews = news.filter((post) => {
    const searchText = newsSearch.trim().toLowerCase();
    const matchesSearch = !searchText || [post.title, post.content, post.author_name]
      .some((value) => value?.toLowerCase().includes(searchText));
    const matchesCategory = newsCategoryFilter === "all" || post.category === newsCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div
        data-testid="dashboard-banner"
        className="relative w-full overflow-hidden rounded-2xl shadow-sm"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(11,17,32,0.80) 0%, rgba(11,17,32,0.45) 45%, rgba(11,17,32,0.05) 100%), url(${SCHOOL_BANNER})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          aspectRatio: "16 / 6",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FACC15]">Residence Hub</p>
          <h1 className="mt-1 font-display text-2xl font-extrabold text-white sm:text-4xl" style={{ textShadow: "0 2px 16px rgba(0,0,0,0.7)" }}>
            Hi {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="mt-1 max-w-md text-sm text-white/85 sm:text-base" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.7)" }}>
            {"Here's what's happening around the hostel today."}
          </p>
        </div>
      </div>

      <section className="space-y-4 border-t-4 border-emerald-500 pt-4" data-testid="dashboard-operations">
        <h2 className="font-display text-xl font-bold">Operational overview</h2>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <OperationMetric to="/attendance" label="People checked in" value={availableResidents.filter((resident) => isResidentOnDuty(resident)).length} testId="dashboard-metric-checked-in" />
          <OperationMetric to="/student-outing" label="Pending outing requests" value={operations?.pending_outings} testId="dashboard-metric-pending-outings" />
          <OperationMetric to="/incidents" label="Open feedback" value={operations?.open_feedback} testId="dashboard-metric-open-feedback" />
          {canAccessSupportRecords(user) && (
            <OperationMetric to="/student-support-records" label="Open student follow-ups" value={operations?.open_support_cases} testId="dashboard-metric-open-support" />
          )}
        </div>
      </section>

      <section className="space-y-4 border-t-4 border-sky-500 pt-4" data-testid="dashboard-events">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" /> Happening this Week</h2>
          <Link to="/events" className="text-sm font-medium text-primary hover:underline" data-testid="view-all-events">View all events</Link>
        </div>
        {weekEvents.length === 0 ? <p className="border-y border-border py-5 text-sm text-muted-foreground">No events this week.</p> : (
          <div className="grid gap-3 sm:grid-cols-2">
            {weekEvents.map((event) => {
              const eventStart = event.start_date || event.date;
              const eventEnd = event.end_date || eventStart;
              const startDate = new Date(`${eventStart}T00:00:00`);
              const endDate = new Date(`${eventEnd}T00:00:00`);
              const todayKey = toDateKey(today);
              const dateLabel = eventStart <= todayKey && eventEnd >= todayKey
                ? "Today"
                : eventStart === eventEnd
                  ? startDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
                  : `${startDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${endDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
              return (
                <article key={event.id} className="border-l-4 border-primary bg-card px-4 py-3" data-testid={`event-item-${event.id}`}>
                  <p className="text-xs font-bold uppercase text-primary">{dateLabel}</p>
                  <h3 className="mt-1 font-display font-bold">{event.title}</h3>
                  <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    {event.time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{event.time}</span>}
                    {event.venue && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{event.venue}</span>}
                  </p>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-4 border-t-4 border-emerald-500 pt-4" data-testid="dashboard-status">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Live Presence</h2>
          <span className="text-xs text-muted-foreground">{availableResidents.filter((resident) => isResidentOnDuty(resident)).length} on-duty</span>
        </div>
        {availableResidents.length === 0 ? <p className="border-y border-border py-5 text-sm text-muted-foreground">No registered users.</p> : (
          <div className="grid gap-x-6 sm:grid-cols-2 lg:grid-cols-3 border-y border-border">
            {availableResidents.map((resident) => {
              const statusLabel = getResidentStatusLabel(resident);
              const isOnDuty = statusLabel === "On-duty";
              return (
                <div key={resident.id} className="flex items-center justify-between gap-3 border-b border-border py-3 last:border-0 sm:[&:nth-last-child(-n+2)]:border-0 lg:[&:nth-last-child(-n+3)]:border-0" data-testid={`resident-status-${resident.id}`}>
                  <div className="min-w-0"><p className="truncate text-sm font-semibold">{resident.short_name}</p><p className="truncate text-xs text-muted-foreground">{resident.role}</p></div>
                  <span className={`shrink-0 text-xs font-semibold ${isOnDuty ? "text-emerald-700" : "text-muted-foreground"}`}>
                    <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${isOnDuty ? "bg-emerald-500" : "bg-slate-300"}`} />{statusLabel}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <div className="flex flex-col gap-6 md:gap-8">
        {/* News feed */}
        <div className="order-2 space-y-5 border-t-4 border-amber-500 pt-4" data-testid="dashboard-news">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <Newspaper className="h-5 w-5 text-primary" /> News Feed
            </h2>
            <Dialog>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="open-news-dialog"><Plus className="h-4 w-4 mr-1" /> Post</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create a post</DialogTitle><DialogDescription>Share news, notices or updates with everyone in the hostel.</DialogDescription></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={newsForm.category} onValueChange={(v) => setNewsForm({ ...newsForm, category: v })}>
                      <SelectTrigger data-testid="news-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input value={newsForm.title} onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })} placeholder="What's the headline?" data-testid="news-title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Content</Label>
                    <Textarea rows={4} value={newsForm.content} onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })} placeholder="Share the details…" data-testid="news-content-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="news-image-input">Picture (optional)</Label>
                    <Input id="news-image-input" type="file" accept="image/*" onChange={(event) => selectImage(event, (image) => setNewsForm((current) => ({ ...current, image })))} data-testid="news-image-input" />
                    {newsForm.image && <img src={newsForm.image} alt="News attachment preview" className="max-h-48 w-full rounded-md border border-border object-contain" />}
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={postNews} data-testid="news-post-submit-btn">Publish</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {news.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
              No posts yet. Be the first to share something!
            </div>
          )}

          {news.map((p) => (
            <article key={p.id} className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm" data-testid={`news-card-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                    {p.author_name?.[0]?.toUpperCase()}
                  </div>
                  <div className="leading-tight">
                    <p className="text-sm font-semibold">{p.author_name}</p>
                    <p className="text-xs text-muted-foreground">{timeAgo(p.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Badge className={`${catColor[p.category] || catColor.General} border-0`}>{p.category}</Badge>
                  <Button variant="ghost" size="icon" onClick={() => openEditPost(p)} aria-label={`Edit ${p.title}`} title="Edit post"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => deletePost(p)} aria-label={`Delete ${p.title}`} title="Delete post"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <h3 className="font-display text-lg font-bold mt-4">{p.title}</h3>
              {p.image && <img src={p.image} alt={`Attachment for ${p.title}`} className="mt-3 max-h-[32rem] w-full rounded-md border border-border object-contain" />}
              {p.content && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{p.content}</p>}
              <div className="flex items-center gap-5 mt-4 pt-3 border-t border-border">
                <button onClick={() => toggleLike(p.id)} data-testid={`like-btn-${p.id}`} className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${p.likes?.includes(user.id) ? "text-red-500" : "text-muted-foreground hover:text-foreground"}`}>
                  <Heart className={`h-4 w-4 ${p.likes?.includes(user.id) ? "fill-current" : ""}`} /> {p.likes?.length || 0}
                </button>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MessageCircle className="h-4 w-4" /> {p.comments?.length || 0}
                </span>
              </div>
              {p.comments?.length > 0 && (
                <div className="mt-3 space-y-2">
                  {p.comments.map((c) => (
                    <div key={c.id} className="text-sm bg-secondary rounded-lg px-3 py-2">
                      <span className="font-semibold">{c.author_name}: </span>{c.text}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <Input
                  value={commentText[p.id] || ""}
                  onChange={(e) => setCommentText({ ...commentText, [p.id]: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && addComment(p.id)}
                  placeholder="Add a comment…"
                  className="h-9"
                  data-testid={`comment-input-${p.id}`}
                />
                <Button size="sm" variant="secondary" onClick={() => addComment(p.id)} data-testid={`comment-submit-${p.id}`}>Send</Button>
              </div>
            </article>
          ))}
        </div>

      </div>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit post</DialogTitle><DialogDescription>Update this shared news post.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={editForm.category} onValueChange={(category) => setEditForm({ ...editForm, category })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Title</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></div>
            <div className="space-y-2"><Label>Content</Label><Textarea rows={4} value={editForm.content} onChange={(e) => setEditForm({ ...editForm, content: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={savePost}>Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OperationMetric({ to, label, value, testId }) {
  return (
    <Link to={to} className="flex min-h-24 items-center justify-between gap-3 border-l-4 border-primary bg-card px-4 py-3 transition-colors hover:bg-secondary/50" data-testid={testId}>
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="font-display text-3xl font-extrabold tabular-nums">{value ?? "—"}</span>
    </Link>
  );
}
